/**
 * 工作流共享状态上下文
 */

import { createContext, useContext, useMemo, type ReactNode } from 'react'

import {
  useIssueWorkflow,
  useTimeConfig,
  useTeamStageOwners,
  useTeamMembers,
  type WorkflowConfig,
  type TimeConfigResult,
} from '@/hooks'

import { getEnabledStages, getCurrentStageIndex, isInParallelDevelopment } from './workflow-utils'

import type { WorkflowStage } from '@company/shared'

// ==================== 类型定义 ====================

interface WorkflowContextValue {
  // 核心数据
  issueId: string
  issueTitle: string
  teamId?: string
  isTeamLeader: boolean

  // 工作流配置
  config: WorkflowConfig | null
  isLoading: boolean

  // 时间配置
  timeConfig: TimeConfigResult | null

  // 计算属性
  enabledStages: WorkflowStage[]
  currentStageIndex: number
  isInParallelDev: boolean

  // 团队数据
  stageOwners: ReturnType<typeof useTeamStageOwners>['data']
  teamMembers: ReturnType<typeof useTeamMembers>['data']

  // 辅助函数
  getEmployeeName: (employeeId: string | null) => string | null
}

const WorkflowContext = createContext<WorkflowContextValue | null>(null)

// ==================== Provider ====================

interface WorkflowProviderProps {
  issueId: string
  issueTitle: string
  teamId?: string
  isTeamLeader?: boolean
  children: ReactNode
}

export function WorkflowProvider({
  issueId,
  issueTitle,
  teamId,
  isTeamLeader = false,
  children,
}: WorkflowProviderProps) {
  const { data: config, isLoading } = useIssueWorkflow(issueId)
  const { data: timeConfig } = useTimeConfig(issueId)
  const { data: stageOwners } = useTeamStageOwners(teamId ?? '')
  const { data: teamMembers } = useTeamMembers(teamId ?? '')

  // 计算启用的阶段
  const enabledStages = useMemo(() => {
    if (!config) {
      return []
    }
    return getEnabledStages(config)
  }, [config])

  // 计算当前阶段索引
  const currentStageIndex = useMemo(() => {
    if (!config) {
      return -1
    }
    return getCurrentStageIndex(config, enabledStages)
  }, [config, enabledStages])

  // 是否处于并行开发
  const isInParallelDev = useMemo(() => {
    if (!config) {
      return false
    }
    return isInParallelDevelopment(config.currentStage)
  }, [config])

  // 根据员工ID获取员工姓名
  const getEmployeeName = (employeeId: string | null): string | null => {
    if (!employeeId || !teamMembers) {
      return null
    }

    const allMembers = [
      ...(teamMembers.developers?.frontend || []),
      ...(teamMembers.developers?.backend || []),
      ...(teamMembers.developers?.design || []),
      ...(teamMembers.developers?.testing || []),
      ...(teamMembers.developers?.product || []),
    ]
    const member = allMembers.find((m) => m.employeeId === employeeId)
    return member?.employee?.name || null
  }

  const value: WorkflowContextValue = {
    issueId,
    issueTitle,
    teamId,
    isTeamLeader,
    config: config ?? null,
    isLoading,
    timeConfig: timeConfig ?? null,
    enabledStages,
    currentStageIndex,
    isInParallelDev,
    stageOwners,
    teamMembers,
    getEmployeeName,
  }

  return <WorkflowContext.Provider value={value}>{children}</WorkflowContext.Provider>
}

// ==================== Hook ====================

export function useWorkflowContext() {
  const context = useContext(WorkflowContext)
  if (!context) {
    throw new Error('useWorkflowContext must be used within a WorkflowProvider')
  }
  return context
}
